import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export type ThemePreference = 'dark-mode' | 'light-mode';

interface ThemePreferenceResponse {
  themePreference: ThemePreference;
}

@Injectable({ providedIn: 'root' })
export class ThemePreferenceService {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly http = inject(HttpClient);
  private readonly preferenceSubject = new BehaviorSubject<ThemePreference>(
    this.normalize(this.document.documentElement.getAttribute('data-theme'))
  );

  readonly preference$ = this.preferenceSubject.asObservable();

  get currentPreference(): ThemePreference {
    return this.preferenceSubject.value;
  }

  get alternatePreference(): ThemePreference {
    return this.currentPreference === 'dark-mode' ? 'light-mode' : 'dark-mode';
  }

  applyPreference(preference: unknown): ThemePreference {
    const normalizedPreference = this.normalize(preference);
    this.document.documentElement.setAttribute('data-theme', normalizedPreference);
    this.preferenceSubject.next(normalizedPreference);
    return normalizedPreference;
  }

  updatePreference(preference: ThemePreference): Observable<ThemePreference> {
    return this.http.put<ThemePreferenceResponse>(
      `${environment.apiUrl}user/update/theme`,
      { themePreference: preference }
    ).pipe(map((response) => this.normalize(response.themePreference)));
  }

  private normalize(preference: unknown): ThemePreference {
    return preference === 'light-mode' ? 'light-mode' : 'dark-mode';
  }
}
